import { Injectable } from '@angular/core';
import { ApiHttpClient } from 'ng2-app/http/clients/api';
import { forkJoin, Observable } from 'rxjs';
import { ScoringGroup } from 'ng2-app/models/pulse/scoring-group.model';
import {
  LenderUserScoreWithAverage
} from 'ng2-app/models/pulse/lender-user-scores.model';
import { TeamRealTimeScoresResponse } from 'ng2-app/models/pulse/team-scores.model';
import {
  RealTimeScoresByGroupWithScoringGroup,
  LenderUserRealTimeScores,
  LenderUserRealTimeScoresWithScoringGroup,
  LenderUserHistoricalScores,
  LenderUsersWithScoringGroups,
} from 'ng2-app/models/pulse/scores.model';
import { PulseHistoricalParams} from 'ng2-app/models/pulse/pulse.type';
import { ScoringHistoryDateRange } from 'ng2-app/models/pulse/scoring-history-date-range.model';
import { Team } from 'app/models/dto/lender-user.model';

@Injectable({
  providedIn: 'root'
})
export class PulseService {
  constructor(private http: ApiHttpClient) { }

  private historicalQueryBuilder(params: PulseHistoricalParams): string {
    const queryParams = new URLSearchParams();

    if (params.excludeZeroScores) {
      queryParams.set('excludeZeroScores', '1');
    }
    if (params.fromDate && this.isValidDateString(params.fromDate)) {
      queryParams.set('fromDate', params.fromDate);
    }
    if (params.toDate && this.isValidDateString(params.toDate)) {
      queryParams.set('toDate', params.toDate);
    }
    if (params.scoringGroupId != null) {
      queryParams.set('scoringGroupId', String(params.scoringGroupId));
    }

    const queryString = queryParams.toString();
    return queryString ? `?${queryString}`: '';
  }

  private isValidDateString(value: string): boolean {
    const match = /^\d{4}-\d{2}-\d{2}$/.test(value);
    if (!match) return false;
    const date = new Date(value + 'T00:00:00');
    return date.toISOString().startsWith(value);
  }

  getScoringHistoryDateRange(lenderUserId: number, scoringGroupId: number): Observable<ScoringHistoryDateRange> {
    return this.http.get<ScoringHistoryDateRange>(
      `/scoring/lender-user/${lenderUserId}/scoring-group/${scoringGroupId}/history-range`
    );
  }

  getScoringGroupsByLenderUserId(lenderUserId: number): Observable<ScoringGroup[]> {
    return this.http.get<ScoringGroup[]>(`/scoring/scoring-groups/${lenderUserId}`);
  }

  getAllScoringGroups(): Observable<ScoringGroup[]> {
    return this.http.get<ScoringGroup[]>('/scoring/scoring-groups');
  }

  getRealTimeScoresByLenderUserId(lenderUserId: number): Observable<LenderUserRealTimeScores> {
    return this.http.get<LenderUserRealTimeScores>(`/scoring/lender-user/${lenderUserId}`);
  }

  getHistoricalScoresByLenderUserId(
    lenderUserId: number,
    params: PulseHistoricalParams = { excludeZeroScores: true }
  ): Observable<LenderUserHistoricalScores[]> {
    const queryString = this.historicalQueryBuilder(params);
    return this.http.get<LenderUserHistoricalScores[]>(`/scoring/lender-user/${lenderUserId}/historical${queryString}`);
  }

  getRealTimeScoresByTeamId(teamId: number): Observable<TeamRealTimeScoresResponse> {
    return this.http.get<TeamRealTimeScoresResponse>(`/scoring/team/${teamId}`);
  }

  getHistoricalScoresByTeamId(
    teamId: number,
    params: PulseHistoricalParams = { excludeZeroScores: true }
  ): Observable<LenderUserHistoricalScores[]> {
    const queryString = this.historicalQueryBuilder(params);
    return this.http.get<LenderUserHistoricalScores[]>(`/scoring/team/${teamId}/historical${queryString}`);
  }

  getScoresByScoringGroupId(scoringGroupId: number, excludeZeroScores = false): Observable<{
    realTimeScores: LenderUserRealTimeScores[];
    historicalScores: LenderUserHistoricalScores[];
  }> {
    return forkJoin({
      realTimeScores: this.getRealTimeScoresByScoreGroupId(scoringGroupId, excludeZeroScores),
      historicalScores: this.getHistoricalScoresByScoreGroupId(scoringGroupId, excludeZeroScores)
    });
  }

  getRealTimeScoresByScoreGroupId(scoringGroupId: number, excludeZeroScores = true): Observable<LenderUserRealTimeScores[]> {
    const queryParams = excludeZeroScores ? '?excludeZeroScores=1' : '';
    return this.http.get<LenderUserRealTimeScores[]>(`/scoring/scoring-group/${scoringGroupId}${queryParams}`);
  }

  getHistoricalScoresByScoreGroupId(scoringGroupId: number, excludeZeroScores = true): Observable<LenderUserHistoricalScores[]> {
    const queryParams = excludeZeroScores ? '?excludeZeroScores=1' : '';
    return this.http.get<LenderUserHistoricalScores[]>(`/scoring/scoring-group/${scoringGroupId}/historical${queryParams}`);
  }

  getCurrentRealTimeScore(scoreByGroup: RealTimeScoresByGroupWithScoringGroup): number {
    const realTimeScores = Object.values(scoreByGroup.realTimeScores);
    return realTimeScores[0].totalGroupScore;
  }

  getTeamsWithScoringGroups(includeZeroMemberTeams = false): Observable<Team[]> {
    const queryParams = includeZeroMemberTeams ? '?includeZeroMemberTeams=1' : '';
    return this.http.get<Team[]>(`/scoring/teams-with-scoring-groups${queryParams}`);
  }

  getLenderUsersWithScoringGroups(): Observable<LenderUsersWithScoringGroups> {
    return this.http.get<LenderUsersWithScoringGroups>('/scoring/users-with-scoring-groups');
  }

  toUserAvatar(lenderUserScore: LenderUserScoreWithAverage | LenderUserRealTimeScoresWithScoringGroup) {
    return {
      id: lenderUserScore.lenderUser.id,
      fullName: lenderUserScore.lenderUser.fullName,
      avatarUrl: lenderUserScore.lenderUser.profilePictureUrl,
    };
  }
}
